import { createContext, useCallback, useContext, useMemo, useState, useEffect, type ReactNode } from 'react';
import {
  login as authLogin,
  logout as authLogout,
  getCurrentUserInfo,
  isAuthenticated as checkAuth,
  type LoginCredentials,
  type UserInfo,
} from '@/services/auth.service';

interface AuthContextData {
  user: UserInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  isRandonneur: () => boolean;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export function AuthProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const check = async () => {
      try {
        const authenticated = await checkAuth();
        if (authenticated) {
          const userInfo = await getCurrentUserInfo();
          setUser(userInfo);
        }
      } catch {
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };
    check();
  }, []);

  const login = useCallback(async (credentials: LoginCredentials) => {
    setIsLoading(true);
    try {
      const userInfo = await authLogin(credentials);
      setUser(userInfo);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      await authLogout();
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const isRandonneur = useCallback(() => user?.groups.includes('Ciclistas') ?? false, [user]);

  // Ver o comentário equivalente no AuthContext do backoffice.
  const valor = useMemo(
    () => ({ user, isAuthenticated: !!user, isLoading, login, logout, isRandonneur }),
    [user, isLoading, login, logout, isRandonneur],
  );

  return <AuthContext.Provider value={valor}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextData {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de AuthProvider');
  return ctx;
}
